
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { google } from 'google-maps';
import { Logger } from '@aws-amplify/core';
import { MachineMarkerOptions } from '../models/MachineMarkerOptions';
import { MachineMarker } from '../models/mqttPayload/MachineMarkerMessage';
import { useMachineStore } from '../stores/machineStore';
import { useMenuStore } from '../stores/menuStore';
import { MachineKey } from '../models/MachineKey';

const logger = new Logger('FlightpathMachineMarker');
@Component({ name: 'FlightpathMachineMarker' })
export default class FlightpathMachineMarker extends Vue {
  @Prop() googleApi!: google;
  @Prop() map!: google.maps.Map<HTMLDivElement>;
  @Prop() marker!: MachineMarkerOptions;
  @Prop() updateMessage!: MachineMarker;
  @Prop() showSelectedMachine!: boolean;
  @Prop() showInfoWindow!: boolean;
  @Prop() allowDuplicateMarkerPositions!: boolean;

  mapMarker = null as google.maps.Marker | null;
  mapCircle = null as google.maps.Marker | null;
  public machineStore = useMachineStore();
  public menuStore = useMenuStore();
  infoListenerHandle: google.maps.MapsEventListener | null = null;
  selectedListenerHandle: google.maps.MapsEventListener | null = null;
  infowindow: google.maps.InfoWindow | null = null;
  markerAnchor = new this.googleApi.maps.Point(24, 24);

  @Watch('marker')
  onMarkerChange(val: MachineMarkerOptions, oldVal: MachineMarkerOptions) {
    const valPos = val.MarkerOptions.position as google.maps.LatLng;
    const oldValPos = oldVal.MarkerOptions.position as google.maps.LatLng;
    if (
      this.mapMarker &&
      (val.MarkerOptions.zIndex != oldVal.MarkerOptions.zIndex ||
        ((valPos.lat != oldValPos.lat || valPos.lng != oldValPos.lng || this.allowDuplicateMarkerPositions) &&
          Date.parse(val.UpdateMessage.SentUTC) > Date.parse(oldVal.UpdateMessage.SentUTC)))
    ) {
      if (this.mapMarker != null) {
        this.mapMarker.setPosition(this.marker.MarkerOptions.position as google.maps.LatLng);
        this.mapMarker.setVisible(true);
        if (val.MarkerOptions.zIndex != null) {
          this.mapMarker.setZIndex(val.MarkerOptions.zIndex as number);
        }
      }

      if (this.mapMarker != null)
        this.mapMarker.setIcon({
          url: this.CreateSvgMarker(this.marker.UpdateMessage.Icon),
          anchor: this.markerAnchor
        });

      if (this.infowindow != null && this.showInfoWindow) this.infowindow.setContent(this.GetInfoContent());
      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId
      ) {
        this.mapCircle?.setPosition(this.mapMarker.getPosition() as google.maps.LatLng);
        this.machineStore.setSelectedMachineMarker(val.UpdateMessage);
        this.map.setCenter(this.mapMarker.getPosition() as google.maps.LatLng);
      } else if (
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId
      ) {
        this.map.setCenter(this.mapMarker.getPosition() as google.maps.LatLng);
      }
    } else if (this.mapMarker && val.UpdateMessage.IsOnline != oldVal.UpdateMessage.IsOnline) {
      if (this.mapMarker != null)
        this.mapMarker.setIcon({
          url: this.CreateSvgMarker(this.marker.UpdateMessage.Icon),
          anchor: this.markerAnchor
        });
    }
  }
  private getDataURI(iconBase64: string) {
    return 'data:image/svg+xml;base64, ' + iconBase64;
  }

  @Watch('machineStore.selectedMachine')
  onSelectedMachineChange(value: MachineKey): void {
    if (value == null) {
      if (this.mapCircle != null) {
        this.mapCircle.setMap(null);
      }
    } else {
      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        value != null &&
        value.machineId == this.updateMessage.Key.machineId
      ) {
        this.mapMarker.setZIndex(99);
        const circleSymbol: google.maps.Symbol = this.CalculateCircleSymbol();
        if (this.mapCircle != null) {
          this.mapCircle.setIcon(circleSymbol);
          this.mapCircle.setPosition(this.mapMarker.getPosition() as google.maps.LatLng);
          this.mapCircle.setZIndex(50);
          this.mapCircle.setMap(this.map);
        } else {
          this.mapCircle = new this.googleApi.maps.Marker({
            icon: circleSymbol,
            position: this.mapMarker.getPosition() as google.maps.LatLng,
            map: this.map,
            zIndex: 50
          });
        }

        const position = {
          lat: this.updateMessage.Location.Location.Latitude,
          lng: this.updateMessage.Location.Location.Longitude
        };
        this.map.setCenter(position as unknown as google.maps.LatLng);
      } else {
        if (this.mapCircle != null) {
          this.mapCircle.setMap(null);
        }
        if (this.mapMarker != null) this.mapMarker.setZIndex(1);
      }
    }
  }

  private AddInfoListner() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.mapMarker != null && this.showInfoWindow) {
      this.infoListenerHandle = this.mapMarker.addListener('click', () => {
        if (this.infowindow != null) this.infowindow.open(this.map, this.mapMarker as google.maps.MVCObject);
      });
    }
  }

  private AddSelectedListner() {
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
    if (this.mapMarker != null) {
      this.selectedListenerHandle = this.mapMarker.addListener('click', () => {
        this.machineStore.setSelectedMachine(this.marker.Machine);
        this.menuStore.setupMachineMenuState();
      });
    }
  }

  destroyed() {
    this.destroyResources();
  }
  private destroyResources() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
    if (this.mapMarker) {
      this.mapMarker.setVisible(false);
      this.mapMarker.setMap(null);
    }
    if (this.mapCircle) {
      this.mapCircle.setVisible(false);
      this.mapCircle.setMap(null);
    }
  }

  deactivated() {
    if (this.infoListenerHandle != null) {
      this.infoListenerHandle.remove();
    }
    if (this.selectedListenerHandle != null) {
      this.selectedListenerHandle.remove();
    }
  }

  private CalculateCircleSymbol(): google.maps.Symbol {
    return {
      path: this.googleApi.maps.SymbolPath.CIRCLE,
      strokeOpacity: 0.8,
      strokeWeight: 1.5,
      strokeColor: '#FFFFFF',
      fillColor: '#FFFFFF',
      fillOpacity: 0.5,
      scale: 30.0
    };
  }

  private CreateSvgMarker(iconBase64: string): string {
    const svgstr = atob(iconBase64);
    const parser = new DOMParser();
    const svg = parser.parseFromString(svgstr, 'image/svg+xml');
    const svgTag = svg.getElementsByTagName('svg')[0];

    // svg transform attribute not supported by safari, we must use an inner <g> element for our rotation to work on iOS
    // see https://stackoverflow.com/questions/48248512/svg-transform-rotate180-does-not-work-in-safari-11
    const innerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    innerGroup.innerHTML = svgTag.innerHTML;

    // set height and width equal so icon is not clipped off when rotated
    svgTag.setAttribute('width', '48');
    svgTag.setAttribute('height', '48');
    // rotate image, scale down so icon is not clipped off at corners, translate icon to center it on gps point
    innerGroup.setAttribute('transform', `scale(0.9, 0.9) translate(1.6 2.4) rotate(${this.updateMessage.Location.Location.Bearing} 16 24)`);

    svgTag.innerHTML = '';
    svgTag.appendChild(innerGroup);

    const icon = btoa(new XMLSerializer().serializeToString(svg));

    return this.getDataURI(icon);
  }

  mounted() {
    this.machineStore = useMachineStore();
    this.menuStore = useMenuStore();

    const circleSymbol: google.maps.Symbol = this.CalculateCircleSymbol();
    this.infowindow = this.GenerateInfoWindow();
    const zIndex = this.marker.MarkerOptions.zIndex != null ? this.marker.MarkerOptions.zIndex : 1;

    this.mapMarker = new this.googleApi.maps.Marker({
      position: this.marker.MarkerOptions.position,
      clickable: true,
      map: this.map,
      icon: {
        url: this.CreateSvgMarker(this.marker.UpdateMessage.Icon),
        anchor: this.markerAnchor
      },
      title: this.marker.MarkerOptions.title,
      zIndex: zIndex
    });
    this.AddInfoListner();
    this.AddSelectedListner();

    if (
      this.showSelectedMachine &&
      this.machineStore.selectedMachine != null &&
      this.machineStore.selectedMachine.machineId == this.updateMessage.Key.machineId
    ) {
      this.mapCircle = new this.googleApi.maps.Marker({
        icon: circleSymbol,
        position: this.marker.MarkerOptions.position,
        map: this.map,
        zIndex: 50
      });
      this.map.setCenter(this.marker.MarkerOptions.position as google.maps.LatLng);
    }

    logger.info('Machine Added', this.marker.Machine.machineId);
  }

  private GenerateInfoWindow() {
    if (!this.showInfoWindow) return null;
    const contentString = this.GetInfoContent();

    const infowindow = new this.googleApi.maps.InfoWindow({
      content: contentString
    });
    return infowindow;
  }

  private GetInfoContent() {
    return `<div id="content">
      <h1 id="firstHeading" class="firstHeading">Machine Status</h1>
      <div id="bodyContent">
      <p><b>Machine: ${this.updateMessage.Key.MachineName}</b></p>
      <p><b>lattitude: ${this.updateMessage.Location.Location.Latitude}</b></p>
      <p><b>Longitude: ${this.updateMessage.Location.Location.Longitude}</b></p>
      <p><b>Bearing: ${Math.abs(this.updateMessage.Location.Location.Bearing).toFixed(4)}</b></p>
      <p><b>Speed: ${this.updateMessage.Location.Location.Speed.toFixed(2)}</b></p>
      </div>
      </div>`;
  }
}
